import type {
  ShiftSegment,
  RollupWithTagsAndCountsAndRange,
  Process,
  TimeRangeFinite,
} from "../../types/types";
import { ONE_DAY_OF_MS, ONE_HOUR_OF_MS, ONE_SECOND_OF_MS } from "../timeUtils";
import { LOSS_CATEGORIES_STRINGS } from "../../constants/constants";
import { CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING } from "../../constants/customers";
import { useSelector } from "store/useSelector";
import { shallowEqual } from "react-redux";
import {
  addTrendChartBarsArrays,
  filterTrendChartBarsForTrendsPage,
  BiggestDurationRollupType,
  getBiggestDurationTrendChartBarRollup,
  EMPTY_TREND_BAR,
} from "../../components/Dashboard/TrendChart/trendChartUtils";
import {
  EMPTY_BIGGEST_DURATION_ROLLUP,
  sliceRangeAtBoundaries,
  useMapDataDumpToRollup,
} from "../../components/Dashboard/TrendChart/TrendChartContent/trendChartContentUtils";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useGetPageFromPath,
  useTrendsPageFilterState,
  useTrendChartBinSize,
  useFilteredProcesses,
  useFocusRange,
  useTrendChartDataDumps,
  useShifts,
  useVisibleProcesses,
} from "./genericHooks";
import { useLotInfoForSingleProcessId } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useShouldUseDataDumpsForTrendChartBarstacks } from "./whichServerHooks";
import { useTrendChartStore } from "components/Dashboard/TrendChart/TrendChartCardControls/trendChartStore";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useMemo } from "react";
import { useCustomerTimeZoneOffsetMs } from "utils/timeZoneHooks";
import { useTrendBarQueryRangesFromFocusRange } from "components/Dashboard/TrendChart/TrendChartBarsGetter";
import { TrendChartBinSizesEnum } from "types/enums";

export const useShouldHideProfitCategories = (): boolean => {
  const { isTrendsPage } = useGetPageFromPath();
  const {
    isTrendsPageFilteredByTag,
    isTrendsPageFilteredByPnlCategory,
    trendsPageFilterState,
  } = useTrendsPageFilterState();
  const [flightConfig] = useFlightConfig();
  const isTrendsPageFilteredByTagOrCategory =
    isTrendsPage &&
    (isTrendsPageFilteredByTag || isTrendsPageFilteredByPnlCategory);
  const isTrendsPageFilteredByDowntimeCategory =
    isTrendsPageFilteredByTagOrCategory &&
    LOSS_CATEGORIES_STRINGS.includes(String(trendsPageFilterState.pnlCategory));
  const hideProfitCategories = flightConfig.trendChart.hideProfitCategories;
  return Boolean(
    // if we're not on the trends page, hide for some customers
    // if we are on the trends page, don't hide... unless (see below)
    (!isTrendsPage && hideProfitCategories) ||
      // on the trends page,
      // if we're filtered down to a single category or tag, flip shouldHideProfitCategories to true
      // so that the tag takes up the full trend chart height
      isTrendsPageFilteredByDowntimeCategory
  );
};

/**
 * returns the sum of trend charts arrays in the current page, ignoring the filter state
 *
 * @returns {RollupWithTagsAndCountsAndRange[]}
 */
export const useTrendChartBarsArrayUnfiltered =
  (): RollupWithTagsAndCountsAndRange[] => {
    const shouldUseDataDumps = useShouldUseDataDumpsForTrendChartBarstacks();

    const allRollupsMetricTrendChartBarsArrays = useSelector(
      state => state.legacy.allRollupsMetricTrendChartBarsArrays,
      shallowEqual
    );
    const { trendChartBinSize } = useTrendChartBinSize();
    const filteredProcesses = useFilteredProcesses();
    const focusRange = useFocusRange();

    const rollupsFromDataDumps = useRollupsArrayFromDataDumps();

    return useMemo(() => {
      const filteredProcessIds = filteredProcesses.map(process => process.id);
      return shouldUseDataDumps
        ? rollupsFromDataDumps
        : addTrendChartBarsArrays({
            allRollupsMetricTrendChartBarsArrays,
            trendChartBinSize,
            visibleProcessIds: filteredProcessIds,
            focusRange,
          }).map(bar => ({
            ...bar,
            cumulativeOutput:
              rollupsFromDataDumps.find(r => r.range.ge === bar.range.ge)
                ?.cumulativeOutput ?? 0,
          }));
    }, [
      allRollupsMetricTrendChartBarsArrays,
      filteredProcesses,
      focusRange,
      rollupsFromDataDumps,
      shouldUseDataDumps,
      trendChartBinSize,
    ]);
  };

/**
 * returns the sum of trend charts arrays in the current page & filter state
 *
 * @param isTrendsPage pass this in if the component is used in a story, where we can't access it from the path
 */
export const useTrendChartBarsArray = (
  isTrendsPage: boolean | null = null
): RollupWithTagsAndCountsAndRange[] => {
  const trendChartBarsArrayFilteredByProcess =
    useTrendChartBarsArrayUnfiltered();

  const focusRange = useFocusRange();
  const { trendChartBinSize } = useTrendChartBinSize();
  const { isTrendsPage: isTrendsPageFromPath } = useGetPageFromPath();
  const { trendsPageFilterState } = useTrendsPageFilterState();

  const filteredShifts = useFilteredShifts();
  const trendChartBarsArray = useMemo(() => {
    const shouldFilterTrendChartForTrendsPage =
      (isTrendsPage || isTrendsPageFromPath) &&
      trendChartBarsArrayFilteredByProcess.length > 0;

    return shouldFilterTrendChartForTrendsPage
      ? (() => {
          const shiftRanges = filteredShifts.map(shift => shift.range);

          const filtered = filterTrendChartBarsForTrendsPage({
            trendChartBarsArrayFilteredByProcess,
            trendsPageFilterState,
            trendChartBinSize,
            focusRange,
            shiftRanges,
          });

          return filtered;
        })()
      : trendChartBarsArrayFilteredByProcess;
  }, [
    filteredShifts,
    focusRange,
    isTrendsPage,
    isTrendsPageFromPath,
    trendChartBarsArrayFilteredByProcess,
    trendChartBinSize,
    trendsPageFilterState,
  ]);
  return trendChartBarsArray;
};

/**
 * get the rollup with the biggest duration, to use for scaling other bars against
 */
export const useBiggestDurationTrendChartBar =
  (): BiggestDurationRollupType | null => {
    const { isTrendsPage } = useGetPageFromPath();
    const { trendChartBinSizeMs } = useTrendChartBinSize();
    const rollupMetricTrendChartBarsArray =
      useTrendChartBarsArray(isTrendsPage);

    const [{ trendChart }] = useFlightConfig();

    const shouldHideProfitCategories = useShouldHideProfitCategories();
    const shouldUseDataDumpsForTrendChartBarstacks =
      useShouldUseDataDumpsForTrendChartBarstacks();
    const rollupsFromDataDumps = useRollupsArrayFromDataDumps();
    const rollupsToUse = shouldUseDataDumpsForTrendChartBarstacks
      ? rollupsFromDataDumps
      : rollupMetricTrendChartBarsArray;
    // customers who hide profit categories always scales to 1 hour (= bin size) on dashboard and grid pages
    return trendChart.hideProfitCategories && !isTrendsPage
      ? {
          ...EMPTY_BIGGEST_DURATION_ROLLUP,
          duration: trendChartBinSizeMs,
        }
      : getBiggestDurationTrendChartBarRollup({
          rollupMetricTrendChartBarsArray: rollupsToUse,
          shouldHideProfitCategories,
          shouldUseDataDumpsForTrendChartBarstacks,
          extraLossCategories: trendChart.extraLossCategories,
        });
  };

/**
 * Returns shifts filtered from the trends page filter state.
 * We only use them in 24hr-mode on trends page,
 * when trend chart bar bin size = shift range.
 * @returns {ShiftSegment[]}
 */
export const useFilteredShifts = (): ShiftSegment[] => {
  const shifts = useShifts();
  const focusRange = useFocusRange();
  const { isTrendsPage } = useGetPageFromPath();
  const { isTrendsPageFilteredByShift, trendsPageFilterState } =
    useTrendsPageFilterState();
  return useMemo(() => {
    // don't bother filtering shifts if we don't need to
    return isTrendsPage && isTrendsPageFilteredByShift && focusRange
      ? shifts.filter(
          shift =>
            // show the shift that starts after the focus range start (allow buffer of 1x range on either side)
            shift?.range?.ge >= focusRange.ge - 3 * ONE_DAY_OF_MS &&
            // and starts before the focus range end
            shift?.range?.ge < focusRange.lt + 3 * ONE_DAY_OF_MS &&
            shift?.shift?.name === trendsPageFilterState.shiftName
        )
      : // return empty array if we don't need filtered shifts
        [];
  }, [
    focusRange,
    isTrendsPage,
    isTrendsPageFilteredByShift,
    shifts,
    trendsPageFilterState.shiftName,
  ]);
};

/**
 * Should show supervisor context when on a page with one process, or using grid page
 * * Cases for grid page:
 * * 1) SANOFI: shows all processes, but allocation done only through one machine, sanofi_d_001 (decommissioned tenant)
 * * 2) PAI: only one process is shown on grid page, if that process is in thisGroupProcesses, then show allocation tool (decommissioned tenant)
 * * 3) RENAISSANCE: should show only Top 5 Downtimes and not show Supervisor Context
 *
 * @returns {string} processId
 */
export const useProcessIdForSupervisorContext = (): string => {
  const ckey = useCustomerKey();
  const { isHubletPage, isGridLayoutPage, isTrendsPage } = useGetPageFromPath();

  const visibleProcesses = useVisibleProcesses();

  /** on details page, this is the only process;
   * on grid page, this is the first visible process
   */
  const process = isHubletPage || isGridLayoutPage ? visibleProcesses[0] : null;

  const isCustomerWhoUsesSupervisorContext =
    ckey in CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING;

  if (isCustomerWhoUsesSupervisorContext && !isTrendsPage) {
    const processIdsThatUseSupervisorContext =
      CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING[ckey].processConfig.map(
        ({ processId }) => processId
      );

    // Check if on details page and process for this page uses allocation tool
    const isHubletPageAndProcessThatUsesSupervisorContext =
      isHubletPage &&
      process?.id &&
      processIdsThatUseSupervisorContext.includes(process?.id);

    // * Grid Page, Case 1 - only one process in customer uses allocation (one or many processes on grid page)
    const doesOnlyOneProcessUseAllocation =
      processIdsThatUseSupervisorContext.length === 1;
    const isGridPageWithOneProcessAllocated =
      isGridLayoutPage && doesOnlyOneProcessUseAllocation;

    // * Grid Page Case 2 - only one process on page
    const isOneProcessOnPage = visibleProcesses.length === 1;
    const doesProcessUseAllocation =
      process && processIdsThatUseSupervisorContext.includes(process.id);
    const isGridPageWithOneProcessOnPage =
      isGridLayoutPage &&
      isOneProcessOnPage &&
      // check if this process uses allocation
      doesProcessUseAllocation;

    return isHubletPageAndProcessThatUsesSupervisorContext && process?.id
      ? process.id
      : isGridPageWithOneProcessAllocated
        ? processIdsThatUseSupervisorContext[0]
        : isGridPageWithOneProcessOnPage
          ? (process as Process).id
          : "";
  }
  return "";
};

/**
 * convert data dumps into rollups (to be used in e.g. trend chart bars)
 * find the required data dumps using the focus range + the trend chart bin size
 */
function useRollupsArrayFromDataDumps(): RollupWithTagsAndCountsAndRange[] {
  const focusRange = useFocusRange();
  const trendChartBarRanges = useTrendChartBarRanges();

  const dataDumpRollupsWithCumulativeOutputs =
    useDataDumpRollupsWithCumulativeOutputs();

  return useMemo(() => {
    if (!focusRange) {
      return [];
    }

    // return the data dumps we have, plus empty bars for the ones we don't have
    return trendChartBarRanges.map(
      (range, idx) =>
        dataDumpRollupsWithCumulativeOutputs[idx] || {
          ...EMPTY_TREND_BAR,
          qualityLoss: 0,
          range,
        }
    );
  }, [dataDumpRollupsWithCumulativeOutputs, focusRange, trendChartBarRanges]);
}

/** ranges used for trend barstacks (can be different from ranges used for trend counts when e.g. counts from Midgard but aggregates from Askr - this happens when flightConfig.general.hidePnlLosses = true, e.g. https://localhost:3000/chem/dashboard/All?view=tl) */
export function useTrendChartBarRangesForBarstacks() {
  const { trendChartBarsRanges: trendChartBarsRangesFromQuery } =
    useTrendBarQueryRangesFromFocusRange(false);
  const allRollupsMetricTrendChartBarsArrays = useSelector(
    state => state.legacy.allRollupsMetricTrendChartBarsArrays
  );
  const trendChartBarsRangesFromAggregates =
    Object.values(allRollupsMetricTrendChartBarsArrays)[0]?.map(d => d.range) ??
    [];

  // sometimes Askr returns an array of aggregates which don't line up with the expected bar ranges from the requested range - in this case, don't hide any data; show a bar for each aggregate
  const shouldUseDataDumpsForTrendChartBarstacks =
    useShouldUseDataDumpsForTrendChartBarstacks(); // this only applies to Askr data, so ignore if trend chart uses Midgard data

  let trendChartBarsRanges: TimeRangeFinite[] = trendChartBarsRangesFromQuery;
  if (
    !shouldUseDataDumpsForTrendChartBarstacks &&
    trendChartBarsRangesFromAggregates.length >
      trendChartBarsRangesFromQuery.length
  ) {
    trendChartBarsRanges = trendChartBarsRangesFromAggregates;
  }

  // on trends page we need to display bars according to trendChartBinSize (on other pages it's always hourly bars) and whether we're filtered by shift

  // - if we're not displaying hourly bars, always show the selected bin size e.g. binSize = day => show one bar per day (even in filtered-by-shift mode)
  const { trendChartBinSize } = useTrendChartBinSize();
  const trendChartBarsRangesTrendsPage = useTrendChartBarRanges();
  if (trendChartBinSize !== TrendChartBinSizesEnum.hours) {
    // we are on trends page with "Display in" set to something other than "hours"
    // so return TrendChartBarRanges which accounts for trends page bin size change
    trendChartBarsRanges = trendChartBarsRangesTrendsPage;
  }

  // - if we're displaying hourly bars, in shift mode, display one bar per shift (so here we find the shifts within the focus range)
  const { isTrendsPageFilteredByShift } = useTrendsPageFilterState();
  const filteredShifts = useFilteredShifts();
  const focusRange = useFocusRange();

  if (isTrendsPageFilteredByShift) {
    const shiftsInFocusRange = focusRange
      ? filteredShifts.filter(
          shift =>
            shift.range.ge >= focusRange.ge && shift.range.lt <= focusRange.lt
        )
      : [];
    if (trendChartBinSize === TrendChartBinSizesEnum.hours) {
      trendChartBarsRanges = shiftsInFocusRange.map(shift => shift.range);
    } else if (trendChartBinSize === TrendChartBinSizesEnum.weeks) {
      // in weeks mode, bars start at ge = shift ge
      let matchingShifts = trendChartBarsRangesTrendsPage
        .map(range => {
          // find first shift with the same day as the bar
          return shiftsInFocusRange.find(
            shift =>
              new Date(shift.range.ge).setHours(0, 0, 0, 0) ===
              new Date(range.ge).setHours(0, 0, 0, 0)
          );
        })
        .filter(Boolean) as ShiftSegment[];

      // sometimes we don't have shifts on every day, e.g. ravenjest has "no shift scheduled" 'shifts' on weekends, so if we didn't find any matches, expand the search range
      if (matchingShifts.length === 0) {
        matchingShifts = trendChartBarsRanges
          .map(range => {
            // find first shift which starts within N days of the bar
            return shiftsInFocusRange.find(
              shift =>
                Math.abs(
                  new Date(shift.range.ge).setHours(0, 0, 0, 0) -
                    new Date(range.ge).setHours(0, 0, 0, 0)
                ) <
                4 * ONE_DAY_OF_MS
            );
          })
          .filter(Boolean) as ShiftSegment[];
      }
      trendChartBarsRanges = matchingShifts.map((shift, idx, shifts) => {
        const nextShift = shifts[idx + 1];
        return {
          ge: shift?.range.ge ?? 0,
          lt: nextShift?.range.ge ?? shift?.range.lt ?? 0,
        };
      });
    }
  }

  return trendChartBarsRanges;
}

/** array of ranges of the visible trend chart bars (includes empty bars) */
export function useTrendChartBarRanges(): TimeRangeFinite[] {
  const focusRange = useFocusRange();
  const { trendChartBinSizeMs } = useTrendChartBinSize();
  const { shouldAlwaysStartTrendChartBarsOnHour } = useTrendChartStore();
  /** ! timezoneOffsetMs here is customerTimeZoneOffsetMs, don't confuse with timeZoneMsOffset = useTimeZoneMsOffset()  */
  const timezoneOffsetMs = useCustomerTimeZoneOffsetMs();
  const { isTier0Page } = useGetPageFromPath();

  return useMemo(() => {
    if (
      !focusRange ||
      // ? TECHDEBT focusRange is initially set to Date.now() somewhere... in this case it's missing __typename property
      Math.abs(focusRange.ge - Date.now()) <
        10 *
          ONE_SECOND_OF_MS /* small tolerance because trend chart won't load during this time i.e. for 10s when the shift changes */ ||
      (!timezoneOffsetMs && timezoneOffsetMs !== 0)
    ) {
      return [];
    }
    if (isTier0Page) {
      // tier0 page uses simple slicing on the hour, e.g. if it starts on :30 each bar will start on :30
      // slice focusRange into hours, start on focusRange.ge and add hours until you reach focusRange.lt
      const numHours = Math.ceil(
        (focusRange.lt - focusRange.ge) / ONE_HOUR_OF_MS
      );
      const hours = Array.from({ length: numHours }, (_v, k) => k).map(hour => {
        const ge = focusRange.ge + hour * ONE_HOUR_OF_MS;
        const lt = ge + ONE_HOUR_OF_MS;

        return {
          ge,
          lt,
        };
      });
      return hours;
    }

    // * if shift starts AND ends outside the hour mark, we need an extra bar (to show two half-bars on either side)
    const doesShiftStartOnHour = focusRange.ge % ONE_HOUR_OF_MS === 0;
    const doesShiftEndOnHour = focusRange.lt % ONE_HOUR_OF_MS === 0;
    const doesShiftStartAndEndOffTheHour =
      !doesShiftStartOnHour && !doesShiftEndOnHour;

    // if customer has 8.5h shift, show 9 bars (Math.ceil)
    const numBarsInFocusRange =
      Math.ceil((focusRange.lt - focusRange.ge) / trendChartBinSizeMs) +
      (doesShiftStartAndEndOffTheHour && shouldAlwaysStartTrendChartBarsOnHour
        ? 1
        : 0);

    // if shouldAlwaysStartTrendChartBarsOnHour is true: if the focus range doesn't start on the hour,
    // the first bar's range will end on the hour, and the second bar's range will start on the hour
    const doesFocusRangeStartOnHour = focusRange.ge % ONE_HOUR_OF_MS === 0;
    const shouldSliceOnHour =
      shouldAlwaysStartTrendChartBarsOnHour && !doesFocusRangeStartOnHour;

    // populate the bar ranges (including empty ones)
    const ranges = [
      ...Array(isNaN(numBarsInFocusRange) ? 0 : numBarsInFocusRange),
    ].map((_, idx, array) => {
      let range = {
        ge: focusRange.ge + idx * trendChartBinSizeMs,
        lt: focusRange.ge + (idx + 1) * trendChartBinSizeMs,
      };

      const first = idx === 0;
      const last = idx === array.length - 1;
      if (shouldSliceOnHour) {
        // the first bar must end on the hour,
        if (first) {
          range = {
            ...range,
            lt: range.lt - (range.lt % ONE_HOUR_OF_MS),
          };
          // the last bar must start on the hour, and end on the shift end
        } else if (last) {
          range = {
            ...range,
            ge: range.ge - (range.ge % ONE_HOUR_OF_MS),
            lt: focusRange.lt,
          };
          // all other bars must start and end on the hour
        } else {
          range = {
            ...range,
            ge: range.ge - (range.ge % ONE_HOUR_OF_MS),
            lt: range.lt - (range.lt % ONE_HOUR_OF_MS),
          };
        }
      }

      const rangesSliced = sliceRangeAtBoundaries({
        range,
        min: focusRange.ge,
        max: focusRange.lt,
      });

      return rangesSliced;
    });
    return ranges;
  }, [
    focusRange,
    shouldAlwaysStartTrendChartBarsOnHour,
    timezoneOffsetMs,
    trendChartBinSizeMs,
    isTier0Page,
  ]);
}

/** data dumps mapped to rollups, so we can use them for trend chart bars */
function useTrendChartDataDumpRollups(): RollupWithTagsAndCountsAndRange[] {
  const dataDumps = useTrendChartDataDumps();
  const mapDataDumpToRollup = useMapDataDumpToRollup();
  return useMemo(() => {
    // map data dumps into rollups
    return [...dataDumps]
      .sort((a, b) => a.range.ge - b.range.ge)
      .map(dataDump => mapDataDumpToRollup(dataDump));
  }, [dataDumps, mapDataDumpToRollup]);
}

/** data dump rollups with cumulative outputs, so we can show the trend line */
function useDataDumpRollupsWithCumulativeOutputs(): RollupWithTagsAndCountsAndRange[] {
  const lotInfo = useLotInfoForSingleProcessId();
  const dataDumpSinceLotStart = useSelector(
    state => state.legacy.dataDumpSinceLotStart
  );
  const [
    {
      general: { enableLotInfoModule },
    },
  ] = useFlightConfig();

  const dataDumpRollups = useTrendChartDataDumpRollups();
  return useMemo(() => {
    return dataDumpRollups.reduce((acc, rollup, idx) => {
      const wasLotCompletedThisHour =
        lotInfo.lotStartDateMs &&
        lotInfo.lotStartDateMs >= rollup.range.ge &&
        lotInfo.lotStartDateMs < rollup.range.lt;

      const wasLotCompletedAfterThisHour =
        lotInfo.lotStartDateMs && lotInfo.lotStartDateMs >= rollup.range.lt;

      const prevCumulativeOutput =
        acc[idx - 1]?.cumulativeOutput || dataDumpSinceLotStart?.netOutput || 0; // start with the previous output, or 0 if it's the first one // 0 if no lot progress, else we start with the count since the lot started
      // (cumulativeOutputSinceLotStart = output between [lotStart, focusRangeStart])

      const cumulativeOutput = enableLotInfoModule
        ? wasLotCompletedAfterThisHour
          ? // if lot was completed AFTER this hour, we show 0 (=> only show the current lot counts)
            0
          : wasLotCompletedThisHour
            ? // if lot was completed THIS hour, restart the count at the count from the dataDumpSinceLotStart
              dataDumpSinceLotStart?.netOutput || 0
            : // if lot was completed BEFORE this hour, add to the previous bar's cumulative output
              prevCumulativeOutput + (rollup?.netOutput || 0)
        : prevCumulativeOutput + (rollup?.netOutput || 0);

      return [
        ...acc,
        {
          ...rollup,
          cumulativeOutput,
        },
      ];
    }, [] as RollupWithTagsAndCountsAndRange[]);
  }, [
    dataDumpRollups,
    dataDumpSinceLotStart?.netOutput,
    enableLotInfoModule,
    lotInfo.lotStartDateMs,
  ]);
}
